import { CancellationNotice } from '../account/CancellationNotice';
import { RenewalNotice } from '../account/RenewalNotice';
import type { MyMembershipResponse } from '@league/shared/contract';
import { Button, CountBird, LINK_CLASS } from '@league/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from 'react';

import type { Actor } from '../../lib/auth';
import { apiFetch } from '../../lib/api';
import { ChooseMembership } from './ChooseMembership';

/**
 * BETWEEN BEING APPROVED AND BEING A DIRECTOR.
 *
 * The funnel has one step the app used to skip: she is screened, interviewed and approved by a
 * person, accepts the invite and sets a password — and then chooses how she pays. Until she has,
 * there is no product to show her, because a director who has not paid has no leagues, no roster
 * and nothing to do.
 *
 * So this sits above the shells and renders exactly one screen. It is not a nag or a banner over a
 * working app; it is the only thing there, which is the honest shape for a state with nothing
 * behind it.
 *
 * THREE PEOPLE PASS STRAIGHT THROUGH.
 *
 * The commissioner, who is staff rather than a member. A player, who is not a subscriber and whose
 * director's billing is none of her business. And a LAPSED director — she has months of leagues,
 * standings and player records to read, nothing was deleted, and putting a paywall over her own
 * history would make "we delete nothing" a promise with no substance. She sees everything and the
 * server refuses every save.
 *
 * ⚠ THIS IS NAVIGATION, NOT AUTHORIZATION. `assertMaySave` in the repository layer is what actually
 * refuses the write, and it would refuse it with this file deleted. What this buys is a person
 * being shown the one thing they can do instead of an error on a screen they should not have
 * reached.
 */
export function MembershipGate({
  actor,
  brand,
  onSignOut,
  children,
}: {
  actor: Actor;
  brand: string;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const isDirector = actor.role === 'director';
  const location = useLocation();
  // Settings owns its membership panel; other routes retain the global notice.
  const activationReturn = new URLSearchParams(location.search).get('membership') === 'started';
  const settingsOwnsNotice = location.pathname.replace(/\/$/, '') === '/director/settings';

  const mine = useQuery({
    queryKey: ['billing', 'me'],
    queryFn: () => apiFetch<MyMembershipResponse>('/api/billing/me'),
    refetchInterval: 30_000,
    enabled: isDirector,
  });

  if (!isDirector) return <>{children}</>;

  /*
    Nothing is rendered while the answer is unknown. Showing the app and then replacing it a beat
    later would flash her own dashboard at somebody who cannot use it yet.
  */
  if (mine.isPending) return null;

  /*
    An error here must not lock her out. If the check itself is broken, the server still refuses
    every write — so falling through to the app is the safe direction, and the failure shows up as
    a refusal on a save rather than as an account that will not open.
  */
  if (mine.isError) return <>{children}</>;

  const status = mine.data?.subscription?.status ?? null;
  const started = status !== null && status !== 'incomplete';

  // Honor older checkout attempts whose immutable return URL still points to Settings.
  // The query string is navigation intent only; verified server membership is required.
  if (activationReturn && settingsOwnsNotice && status === 'active') return <Navigate to="/director" replace />;

  /* A free trial, running or over: the app stays open and the server decides what may be saved. */
  const trial = started ? null : mine.data?.trial ?? null;

  if ((started || trial) && settingsOwnsNotice) return <>{children}</>;

  if (trial) return <MembershipNoticeHost notice={<TrialNotice trial={trial} />}>{children}</MembershipNoticeHost>;

  if (started) {
    const notice = mine.data?.cancellation ? <CancellationNotice cancellation={mine.data.cancellation} firstName={mine.data.firstName} onResume={() => { window.location.href = '/director/settings'; }} /> : mine.data?.renewal ? <RenewalNotice renewal={mine.data.renewal} /> : null;
    return (
      <MembershipNoticeHost notice={notice}>{children}</MembershipNoticeHost>
    );
  }

  return (
    <div className="min-h-screen bg-chalk">
      {/*
        The mark, because this is the first screen she sees after accepting the invite and it is
        the moment she stops being an applicant. A page with no wordmark here reads as a form some
        third party is collecting a card on.
      */}
      <header className="flex items-center gap-3 bg-deep px-6 py-4">
        <CountBird size={40} ground="navy" className="shrink-0" />
        <span className="font-display text-[17px] leading-none text-on-deep">{brand}</span>
        {/*
          The only way off this screen, and it has to be here. Everything else in the app is behind
          the same check, so somebody who signed in on a shared machine — or on the wrong account —
          would otherwise have no exit but clearing cookies.
        */}
        <Button variant="secondaryOnDeep" size="inline" className="ml-auto" onClick={onSignOut}>
          Sign out
        </Button>
      </header>
      {activationReturn && status === 'incomplete' ? <p role="status" className="mx-auto max-w-2xl px-6 pt-6 text-[14px] leading-relaxed">We’re confirming your membership. This page will update when payment is verified. If confirmation is delayed, you can continue below to check your existing payment.</p> : null}
      <ChooseMembership founding={mine.data?.membership?.founding ?? false} />
    </div>
  );
}

function TrialNotice({ trial }: { trial: NonNullable<MyMembershipResponse['trial']> }) {
  const said = trial.live
    ? `${trial.daysLeft} ${trial.daysLeft === 1 ? 'day' : 'days'} left in your free trial.`
    : 'Your free trial has ended. Everything you set up is still here.';
  return (
    <p className="rounded-md border border-light bg-paper px-4 py-3 text-[14px]">
      {said} <Link to="/director/settings#membership" className={LINK_CLASS}>Start your membership</Link>
    </p>
  );
}

/*
  ⚠ THE NOTICE BELONGS IN THE PAGE, NOT ABOVE THE APP — owner, 2026-10-04. The gate wraps the whole
  shell, so a notice rendered here sat above the rail and the page, pushed the menu down and floated
  in the corner. The director shell now CLAIMS it and shows it at the top of its own content column.
  Anything without a shell to claim it (a test, the commissioner's screens) still gets it here.
*/
interface NoticeSlot {
  notice: ReactNode;
  claim: (claimed: boolean) => void;
}

const MembershipNoticeSlot = createContext<NoticeSlot | null>(null);

function MembershipNoticeHost({ notice, children }: { notice: ReactNode; children: ReactNode }) {
  const [claimed, setClaimed] = useState(false);
  return (
    <MembershipNoticeSlot.Provider value={{ notice, claim: setClaimed }}>
      {notice && !claimed ? <div className="px-4">{notice}</div> : null}
      {children}
    </MembershipNoticeSlot.Provider>
  );
}

/** For a shell: the membership notice, shown in its own content column. */
export function MembershipNotice({ className }: { className?: string }) {
  const slot = useContext(MembershipNoticeSlot);
  const claim = slot?.claim;
  /* Before paint, so the notice never flashes above the app first. */
  useLayoutEffect(() => {
    claim?.(true);
    return () => claim?.(false);
  }, [claim]);
  return slot?.notice ? <div className={className}>{slot.notice}</div> : null;
}
